import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import NewProjectPage from "@/pages/NewProjectPage";
import SettingsPage from "@/pages/SettingsPage";
import VendorsPage from "@/pages/VendorsPage";
import VendorDetailPage from "@/pages/VendorDetailPage";
import VendorScorecardPage from "@/pages/VendorScorecardPage";
import InboxPage from "@/pages/InboxPage";
import DocumentDetailPage from "@/pages/DocumentDetailPage";
import JoinPage from "@/pages/JoinPage";
import AuthCallback from "@/pages/AuthCallback";
import CreateCompanyPage from "@/pages/CreateCompanyPage";
import OnboardingPage from "@/pages/OnboardingPage";
import GanttPage from "@/pages/GanttPage";
import ReportSchedulesPage from "@/pages/ReportSchedulesPage";
import TemplatesPage from "@/pages/TemplatesPage";
import ProductAnalyticsPage from "@/pages/ProductAnalyticsPage";

// Layout
import AppLayout from "@/components/layout/AppLayout";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-pulse space-y-4">
            <div className="w-16 h-16 bg-gray-200 border-2 border-black mx-auto" />
            <div className="h-4 w-32 bg-gray-200 mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user needs to create a company
  if (user.needs_company && location.pathname !== '/create-company') {
    return <Navigate to="/create-company" replace />;
  }

  // Check if user needs onboarding
  if (user.onboarding_complete === false && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Public Route - redirect to dashboard if already authenticated
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse space-y-4">
          <div className="w-16 h-16 bg-gray-200 border-2 border-black mx-auto" />
          <div className="h-4 w-32 bg-gray-200 mx-auto" />
        </div>
      </div>
    );
  }

  if (user && user.onboarding_complete !== false) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router - handles session_id detection synchronously
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment synchronously during render for session_id
  // This prevents race conditions with auth checking
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/join" element={<JoinPage />} />
      <Route path="/join/:inviteCode" element={<JoinPage />} />
      <Route path="/create-company" element={
        <ProtectedRoute>
          <CreateCompanyPage />
        </ProtectedRoute>
      } />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppLayout>
            <DashboardPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/projects/new" element={
        <ProtectedRoute>
          <NewProjectPage />
        </ProtectedRoute>
      } />
      <Route path="/projects/:projectId" element={
        <ProtectedRoute>
          <AppLayout>
            <ProjectDetailPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/timeline" element={
        <ProtectedRoute>
          <AppLayout>
            <GanttPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <AppLayout>
            <SettingsPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/vendors" element={
        <ProtectedRoute>
          <AppLayout>
            <VendorsPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/vendors/scorecard" element={
        <ProtectedRoute>
          <AppLayout>
            <VendorScorecardPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/vendors/:vendorName" element={
        <ProtectedRoute>
          <AppLayout>
            <VendorDetailPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/inbox" element={
        <ProtectedRoute>
          <AppLayout>
            <InboxPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/reports/schedules" element={
        <ProtectedRoute>
          <AppLayout>
            <ReportSchedulesPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/templates" element={
        <ProtectedRoute>
          <AppLayout>
            <TemplatesPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/analytics/products" element={
        <ProtectedRoute>
          <AppLayout>
            <ProductAnalyticsPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/inbox/:documentId" element={
        <ProtectedRoute>
          <AppLayout>
            <DocumentDetailPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
